import { useState } from 'react';
import { wall, type Route, type Stop } from '@day';
import { useStore } from '../app/store';
import { openCourier, openRoute, openStop, ui } from '../app/ui';
import { dayWords, hourWords, phoneMatches, plural, streetOf, time, timingOf } from '../app/words';
import { keyOf, routeTitle, type Model, type Person } from '../data/model';
import { groupStops, type StopGroup } from '../data/groups';
import { Av, Dot, EmptyHelp, More, nearly, STATUS_TABS, StatusIcon, StatusNow } from '../ui/bits';
import { Icon } from '../ui/Icon';
import { toast } from '../ui/Overlays';
import { FILTERS } from '../app/menus';
import { BulkBar } from './LiveTables';
import { copyText } from './StopBody';
import { WaitingPage } from './Waiting';
import { ImportSheet } from './Import';

/** Stops: every stop today as a table (control-room-web.html, "Stops"). */

const timing = (s: Stop, m: Model) => timingOf(s, m.now, m.zone, m.ifNow.stops.get(keyOf(s)));

const COLS = [
  { k: 'addr', l: 'Address', sort: (s: Stop) => streetOf(s).toLowerCase() },
  { k: 'status', l: 'Status', sort: (s: Stop) => s.at ?? s.eta ?? 1e15 },
  { k: 'timing', l: 'On time', low: true, sort: (s: Stop, m: Model) => timing(s, m)?.rank ?? 9 },
  { k: 'who', l: 'Customer', low: true, sort: (s: Stop) => (s.name ?? '').toLowerCase() },
  { k: 'courier', l: 'Courier', sort: (_s: Stop, m: Model, s2?: Stop) => (m.person((s2 ?? _s).riderId)?.first ?? '') + (s2 ?? _s).routeId },
];

export function stopsRows(m: Model, s: { tab: string; q: string; courier: string | null; sort: { k: string; dir: 1 | -1 } | null; flt?: Set<string> }) {
  const q = s.q.trim().toLowerCase();
  const added = FILTERS.filter((f) => s.flt?.has(f.k));
  const base = m.stops.filter((x) => (!s.courier || x.riderId === s.courier) && added.every((f) => f.f(x)));
  const tab = STATUS_TABS.find((t) => t.k === s.tab) ?? STATUS_TABS[0];
  let rows = base.filter(tab.f).filter((x) => {
    if (!q) return true;
    const r = m.route(x.routeId);
    return [x.address, x.area, x.name, m.person(x.riderId)?.name, r ? routeTitle(r, m.zone) : '', x.notes, x.trackingCode, x.phone].join(' ').toLowerCase().includes(q) || phoneMatches(q, x.phone, m.business?.country ?? null);
  });
  if (s.sort) {
    const col = COLS.find((c) => c.k === s.sort!.k)!;
    const key = (x: Stop) => (col.k === 'courier' || col.k === 'timing' ? col.sort(x, m) : (col.sort as (s: Stop) => string | number)(x));
    rows = [...rows].sort((a, b) => {
      const x = key(a);
      const y = key(b);
      return (x < y ? -1 : x > y ? 1 : 0) * s.sort!.dir;
    });
  }
  return { base, rows };
}

/** Each hour's stops, done and expected: "these stops" at a glance. */
function Hours({ m, rows }: { m: Model; rows: Stop[] }) {
  const timed = rows.filter((s) => (s.at ?? s.eta) != null);
  const untimed = rows.length - timed.length;
  const hourOf = (s: Stop) => wall((s.at ?? s.eta)!, m.zone).hour;
  const nowH = wall(m.now, m.zone).hour;
  const seen = timed.map(hourOf);
  const from = Math.min(9, nowH, ...seen);
  const to = Math.max(from + 5, Math.min(21, Math.max(nowH, ...seen)));
  const H = Array.from({ length: to - from + 1 }, (_, i) => from + i);
  const counts = H.map((h) => timed.filter((s) => hourOf(s) === h).length);
  const max = Math.max(1, ...counts);
  const label = (h: number) => hourWords(h);
  return (
    <div className="card hourcard">
      <div style={{ minWidth: 150 }}>
        <div className="eyebrow">These stops</div>
        <div className="v mono">{rows.length}</div>
        <div className="cap">
          {rows.filter((s) => s.state === 'done').length} delivered{untimed ? ` · ${untimed} not started` : ''}
        </div>
      </div>
      <div className="hours">
        {H.map((h, i) => (
          <div key={h} data-tip={`${label(h)} to ${label(h).replace(/^\d+/, (n) => n)}:59: ${plural(counts[i], 'stop')}${h === nowH ? ', so far and expected' : h > nowH ? ', expected' : ''}`}>
            <b className={h === nowH ? 'now' : ''} style={{ height: Math.max(2, (counts[i] / max) * 50) }} />
            <span>{label(h)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function StopsPage({ m }: { m: Model }) {
  const s = useStore(ui);
  const { base, rows } = stopsRows(m, s);
  const p = s.courier ? m.person(s.courier) : null;
  const keys = rows.map(keyOf);
  const sel = keys.filter((k) => s.sel.has(k));
  const all = keys.length > 0 && sel.length === keys.length;
  const cols = COLS.filter((c) => !s.hideCols.has(c.k));
  const q = s.q.trim();
  const sortBy = (k: string) => ui.set({ sort: s.sort?.k !== k ? { k, dir: 1 } : s.sort.dir === 1 ? { k, dir: -1 } : null });
  const toggle = (k: string) =>
    ui.set((x) => {
      const next = new Set(x.sel);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return { sel: next };
    });
  // Grouped by route or courier (board `routes-web`): each group folds away; what's folded is this visit's only.
  const [shut, setShut] = useState<Set<string>>(() => new Set());
  const grouped = s.group !== 'none' ? groupStops(rows, m.stops, s.group, { route: m.route, rank: (uid) => m.people.findIndex((x) => x.uid === uid), sorted: !!s.sort }) : null;
  const rowOf = (x: Stop) => {
    const k = keyOf(x);
    const on = s.sel.has(k);
    const r = m.route(x.routeId);
    const who = m.person(x.riderId);
    const fin = s.tab === 'all' && (x.state === 'done' || x.state === 'failed');
    const when = timing(x, m);
    return (
      <tr key={k} className={`r${s.rec?.id === k ? ' sel' : ''}${fin ? ' fin' : ''}`} data-stop={k} data-ctx={`stop:${k}`} onClick={() => openStop(k)}>
        <td className="cb" onClick={(e) => e.stopPropagation()}>
          <button className={`cbx${on ? ' on' : ''}`} role="checkbox" aria-checked={on} aria-label={`Select ${streetOf(x)}`} onClick={() => toggle(k)}>
            {on && <Icon name="check" />}
          </button>
        </td>
        <td className="si">
          <StatusIcon s={x} today={m.today} />
        </td>
        {cols.map((c) =>
          c.k === 'addr' ? (
            <td key="addr" className="ad">
              {(x.notes || x.access) && <span className="nmark" data-tip={`Note: ${[x.access, x.notes].filter(Boolean).join(' · ')}`} />}
              <div className="row" style={{ gap: 4 }}>
                <div style={{ minWidth: 0 }}>
                  <div className="addr">{streetOf(x)}</div>
                  <div className="area">{x.area}</div>
                </div>
                <button
                  className="iconbtn sm hcopy"
                  data-tip="Copy the address"
                  aria-label="Copy the address"
                  onClick={(e) => {
                    e.stopPropagation();
                    copyText(x.writtenAddress ?? x.address, 'Address copied');
                  }}
                >
                  <Icon name="copy" size="sm" />
                </button>
              </div>
            </td>
          ) : c.k === 'status' ? (
            <td key="status">
              <StatusNow s={x} m={m} routeName={r ? routeTitle(r, m.zone) : ''} />
            </td>
          ) : c.k === 'timing' ? (
            <td key="timing" className="nw low">
              {when ? (
                <span className={`chip${when.tone ? ` ${when.tone}` : ''}`} data-tip={when.tip}>
                  {when.word}
                </span>
              ) : (
                <span className="ink2">–</span>
              )}
            </td>
          ) : c.k === 'who' ? (
            <td key="who" className="nw low who">
              {x.name ?? <span className="ink2">–</span>}
            </td>
          ) : (
            <td key="courier" className="nw">
              <span className="row" style={{ gap: 8 }}>
                <Av p={who} size="sm" />
                <span style={{ lineHeight: 1.25 }}>
                  {who?.first ?? 'A courier'}
                  {x.state !== 'waiting' && r && (
                    <>
                      <br className="low" />
                      <span className="area low">{routeTitle(r, m.zone)}</span>
                    </>
                  )}
                </span>
              </span>
            </td>
          ),
        )}
        <td className="act" onClick={(e) => e.stopPropagation()}>
          <More menu={`stop:${k}`} />
        </td>
      </tr>
    );
  };
  return (
    <div className="tpage" id="stopsPage">
      <div className="filters">
        <span className="ctl" data-tip="Other days come with the server’s daily totals">
          <Icon name="calendarBlank" />
          Today, {dayWords(m.today).split(' ')[0]}
        </span>
        <span className="vr" />
        <div className="tabs" role="tablist">
          <button className="tab" role="tab" aria-pressed={s.tab === 'wait'} data-tip="Booked before their route exists" onClick={() => ui.set({ tab: 'wait', sel: new Set(), rec: s.rec?.type === 'stop' ? null : s.rec })}>
            <Icon name="stack" />
            Waiting for a route
            <span className="c">{m.waiting.length}</span>
          </button>
          <span className="vr" />
          {STATUS_TABS.map((t) => (
            <button key={t.k} className="tab" role="tab" aria-pressed={s.tab === t.k} onClick={() => ui.set({ tab: t.k, sel: new Set(), rec: s.rec?.type === 'waiting' ? null : s.rec })}>
              <Icon name={t.icon} />
              {t.l}
              <span className="c">{base.filter(t.f).length}</span>
            </button>
          ))}
        </div>
      </div>
      {s.tab === 'wait' ? (
        s.importing ? <ImportSheet m={m} /> : <WaitingPage m={m} />
      ) : (
      <>
      <Hours m={m} rows={rows} />
      <div className="card">
        <div className="tools">
          {sel.length ? (
            <BulkBar m={m} stops={sel.map((k) => m.stop(k)).filter((x): x is Stop => !!x)} />
          ) : (
            <>
              {p && (
                <span className="chip fchip">
                  <Dot p={p} />
                  Courier: {p.first}
                  <button data-tip="Every courier" aria-label="Every courier" onClick={() => ui.set({ courier: null, sel: new Set() })}>
                    <Icon name="x" size="sm" />
                  </button>
                </span>
              )}
              {FILTERS.filter((f) => s.flt.has(f.k)).map((f) => (
                <span key={f.k} className="chip fchip">
                  <Icon name={f.icon} size="sm" />
                  {f.l}
                  <button
                    data-tip="Take this filter off"
                    aria-label={`Take off ${f.l}`}
                    onClick={() =>
                      ui.set((x) => {
                        const flt = new Set(x.flt);
                        flt.delete(f.k);
                        return { flt };
                      })
                    }
                  >
                    <Icon name="x" size="sm" />
                  </button>
                </span>
              ))}
              <button className="ctl" data-menu="addfilter:">
                <Icon name="funnelSimple" />
                Add filter
                <Icon name="caretDown" size="sm" />
              </button>
              <button className="ctl" data-menu="group:" data-tip="Group the stops by route or by courier">
                <Icon name="list" />
                {s.group === 'route' ? 'By route' : s.group === 'courier' ? 'By courier' : 'Group'}
                <Icon name="caretDown" size="sm" />
              </button>
              <div className="field">
                <Icon name="magnifyingGlass" />
                <input id="stopq" placeholder="Search street, customer, courier, note or code" value={s.q} onChange={(e) => ui.set({ q: e.target.value })} autoComplete="off" spellCheck={false} aria-label="Search stops" />
              </div>
            </>
          )}
          <span className="grow" />
          <span className="cap">
            {rows.length} of {m.stops.length}
          </span>
        </div>
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th className="cb">
                  <button
                    className={`cbx${all ? ' on' : ''}`}
                    role="checkbox"
                    aria-checked={all}
                    aria-label={all ? 'Clear these' : 'Select these'}
                    data-tip={all ? 'Clear these' : 'Select these'}
                    onClick={() => ui.set((x) => ({ sel: all ? new Set([...x.sel].filter((k) => !keys.includes(k))) : new Set([...x.sel, ...keys]) }))}
                  >
                    {all && <Icon name="check" />}
                  </button>
                </th>
                <th className="si" />
                {cols.map((c) => (
                  <th
                    key={c.k}
                    className={c.low ? `low ${c.k}` : ''}
                    data-ctx={`col:${c.k}`}
                    data-sortcol={c.k}
                    tabIndex={0}
                    aria-sort={s.sort?.k === c.k ? (s.sort.dir > 0 ? 'ascending' : 'descending') : 'none'}
                    onClick={() => sortBy(c.k)}
                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), sortBy(c.k))}
                  >
                    {c.l}
                    {s.sort?.k === c.k && <Icon name={s.sort.dir > 0 ? 'caretUp' : 'caretDown'} size="sm" />}
                  </th>
                ))}
                <th className="act" />
              </tr>
            </thead>
            <tbody>
              {rows.length ? (
                grouped ? (
                  grouped.flatMap((g) => {
                    const open = !shut.has(g.key);
                    const gk = g.rows.map(keyOf);
                    const on = gk.every((k) => s.sel.has(k));
                    return [
                      <GroupHead
                        key={g.key}
                        m={m}
                        g={g}
                        span={cols.length}
                        open={open}
                        on={on}
                        onFold={() => setShut((x) => { const next = new Set(x); if (next.has(g.key)) next.delete(g.key); else next.add(g.key); return next; })}
                        onSel={() => ui.set((x) => ({ sel: on ? new Set([...x.sel].filter((k) => !gk.includes(k))) : new Set([...x.sel, ...gk]) }))}
                      />,
                      ...(open ? g.rows.map(rowOf) : []),
                    ];
                  })
                ) : (
                  rows.map(rowOf)
                )
              ) : (
                <tr>
                  <td colSpan={cols.length + 3}>
                    <EmptyHelp
                      q={q}
                      title={`No stops ${s.tab === 'all' ? 'today' : `in ${STATUS_TABS.find((t) => t.k === s.tab)?.l}`}${p ? ` for ${p.first}` : ''}.`}
                      tries={[
                        ...nearly(q, m.stops, (x) => `${x.address} ${x.name ?? ''}`).map((x) => ({ icon: 'mapPin', label: streetOf(x), note: `${m.person(x.riderId)?.first ?? ''}`, act: `stop:${keyOf(x)}` })),
                        ...(s.tab !== 'all' || s.courier || s.flt.size ? [{ icon: 'stack', label: 'Search all stops', note: 'Every courier, every status, no filters', act: 'all' }] : []),
                        { icon: 'calendarBlank', label: 'Other days', note: 'In Analytics: each day’s numbers', act: 'reports' },
                        ...(q ? [{ icon: 'x', label: 'Clear the search', act: 'clear' }] : []),
                      ]}
                      onTry={(a) => {
                        if (a.startsWith('stop:')) return openStop(a.slice(5));
                        if (a === 'all') return ui.set({ tab: 'all', courier: null, flt: new Set(), sel: new Set() });
                        if (a === 'clear') return ui.set({ q: '' });
                        if (a === 'reports') return ui.set({ page: 'reports' });
                        toast(a);
                      }}
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      </>
      )}
    </div>
  );
}

/** A route's state in Live's words (LiveTables.tsx), a finished one with its time. */
function StateChip({ m, r, p }: { m: Model; r: Route; p: Person | null }) {
  if (r.state === 'running')
    return (
      <span className="chip">
        <Dot p={p} />
        On a route
      </span>
    );
  if (r.state === 'waiting')
    return (
      <span className="chip">
        <Icon name="circleDashed" size="sm" />
        Waiting to start
      </span>
    );
  if (r.state === 'declined')
    return (
      <span className="chip neg">
        <Icon name="warningCircle" size="sm" />
        Can’t take it
      </span>
    );
  const t = r.completedAt ?? r.endedAt;
  return (
    <span className="chip">
      <Icon name="flagCheckered" size="sm" />
      {r.state === 'ended' ? 'Ended early' : 'Finished'}
      {t != null && ` ${time(t, m.zone)}`}
    </span>
  );
}

/** When a route's expected to be done: running, from the courier's day; waiting, as if it left now. */
function doneAbout(m: Model, r: Route, p: Person | null): string | null {
  if (r.state === 'running') return p?.day?.finishAt ? `Done about ${time(p.day.finishAt, m.zone)}` : null;
  const f = r.state === 'waiting' ? m.ifNow.routes.get(r.id)?.finish : null;
  return f ? `Done about ${time(f, m.zone)} if it left now` : null;
}

/**
 * A group's header row (board `routes-web`): the route or courier, their state, delivered so far and when it's done. The
 * title opens the route (on Live) or the courier's record; right-click is the route's or courier's own menu.
 */
function GroupHead({ m, g, span, open, on, onFold, onSel }: { m: Model; g: StopGroup; span: number; open: boolean; on: boolean; onFold: () => void; onSel: () => void }) {
  const p = g.riderId ? m.person(g.riderId) : null;
  // A courier's: the route running (or the next), else the one finished last.
  const lead = g.routes.find((r) => r.state !== 'done' && r.state !== 'ended') ?? g.routes.at(-1) ?? null;
  const title = g.kind === 'sent' ? 'Sent on their own' : g.kind === 'route' ? (lead ? routeTitle(lead, m.zone) : 'The route') : (p?.name ?? 'A courier');
  const ctx = g.kind === 'sent' ? undefined : `${g.kind}:${g.id}`;
  const when = lead ? doneAbout(m, lead, p) : null;
  const stop = (e: React.MouseEvent) => e.stopPropagation();
  return (
    <tr className="grp" data-ctx={ctx} onClick={onFold}>
      <td className="cb" onClick={stop}>
        <button className={`cbx${on ? ' on' : ''}`} role="checkbox" aria-checked={on} aria-label={on ? `Clear ${title}’s stops` : `Select ${title}’s stops`} data-tip={on ? 'Clear these' : 'Select these'} onClick={onSel}>
          {on && <Icon name="check" />}
        </button>
      </td>
      <td className="si">
        <button className="iconbtn sm" aria-expanded={open} aria-label={open ? `Fold ${title}` : `Show ${title}`} onClick={(e) => (stop(e), onFold())}>
          <Icon name={open ? 'caretDown' : 'caretRight'} size="sm" />
        </button>
      </td>
      <td colSpan={span}>
        <div className="row gline">
          {g.kind !== 'sent' && <Av p={p} size="sm" />}
          {ctx ? (
            <button className="gtitle" data-tip={g.kind === 'route' ? 'Open the route' : 'Open their record'} onClick={(e) => (stop(e), g.kind === 'route' ? openRoute(g.id) : openCourier(g.id))}>
              {title}
            </button>
          ) : (
            <span className="gtitle">{title}</span>
          )}
          {g.kind === 'route' && p && <span className="ink2">{p.first}</span>}
          {g.kind === 'courier' && g.routes.length > 1 && <span className="ink2">{plural(g.routes.length, 'route')}</span>}
          {lead && <StateChip m={m} r={lead} p={p} />}
          <span className="ink2">
            {g.done} of {g.total} delivered
          </span>
          {when && <span className="ink2">{when}</span>}
          <span className="grow" />
          <span className="ink2 mono">{g.rows.length === g.total ? plural(g.total, 'stop') : `${g.rows.length} of ${g.total} stops`}</span>
        </div>
      </td>
      <td className="act" onClick={stop}>
        {ctx && <More menu={ctx} />}
      </td>
    </tr>
  );
}
